"use client";
import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import {
  GemsBadge,
  PerpetualRewardsBadge,
  PointsBadge,
} from "@/app/games/(details)/quest-badges";
import { type GameData, getQuestIcon } from "@/app/games/(details)/types";
import type {
  CampaignQuest,
  CampaignQuestState,
} from "@/components/QuestsV2/types";
import { ListItem, listItemTextVariants } from "@/components/ui/list";
import { Progress } from "@/components/ui/progress";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { cn } from "@/lib/utils";
import { IterationCcw } from "lucide-react";
import { Fragment, useCallback } from "react";

export const Quests = ({ gameData }: { gameData: GameData }) => {
  const { track } = useGamePageAnalytics();
  const {
    allQuests,
    completeQuest,
    inProgressQuest,
    setInProgressQuest,
    onQuestClick,
  } = useGameDetailsContext();

  // Calculate progress based on total rewards earned vs total possible gems
  const totalGemsEarned =
    allQuests?.reduce((sum, quest) => {
      // For completed quests, multiply gems by completion count (for recurring quests)
      if (
        quest.completed &&
        quest.completionCount &&
        quest.completionCount > 0
      ) {
        return sum + quest.gems * quest.completionCount;
      }
      return sum;
    }, 0) ?? 0;

  const totalPossibleGems =
    allQuests?.reduce((sum, quest) => sum + quest.gems, 0) ?? 0;

  // Ensure progress doesn't exceed 100% for recurring quests
  const progressPercentage =
    totalPossibleGems > 0
      ? Math.min((totalGemsEarned / totalPossibleGems) * 100, 100)
      : 0;

  const handleQuestClick = useCallback(
    (quest: CampaignQuestState) => {
      if (quest.completedToday) return;

      track({
        action: Action.PRESSED,
        extras: {
          control: "quest",
          controlLabel: quest.name,
          controlType: ControlType.LIST_ITEM,
          section: GamePageSection.RIGHT_PANEL,
        },
      });

      onQuestClick(quest);
    },
    [track, onQuestClick],
  );

  const handleQuestComplete = useCallback(
    async (quest: CampaignQuest) => {
      const questCompleted = await completeQuest(quest);

      if (questCompleted) {
        track({
          action: Action.SUCCEEDED,
          extras: {
            control: "quest",
            controlLabel: quest.name,
            controlType: ControlType.LIST_ITEM,
            section: GamePageSection.RIGHT_PANEL,
          },
        });

        return;
      }

      track({
        action: Action.FAILED,
        extras: {
          control: "quest",
          controlLabel: quest.name,
          controlType: ControlType.LIST_ITEM,
          section: GamePageSection.RIGHT_PANEL,
        },
      });
    },
    [completeQuest, track],
  );

  return (
    <div className="w-full mt-8">
      <h1 className="font-suisse font-semibold text-2xl leading-8 text-neutral-50 text-center">
        {gameData.details.name} Quests
      </h1>

      <div className="flex items-center gap-5 mt-4">
        <Progress
          value={progressPercentage}
          className="bg-neutral-50/20 flex-1"
        />
        <GemsBadge
          text={`${totalGemsEarned} / ${Math.max(totalPossibleGems ?? 0, totalGemsEarned ?? 0)}`}
          completed={totalGemsEarned >= totalPossibleGems}
          className="bg-neutral-950/40"
        />
      </div>

      <div className="flex-1 h-px bg-foreground opacity-5 mt-4" />
      {allQuests?.map((quest) => {
        const QuestIcon = getQuestIcon(quest.type, quest.categories ?? []);
        return (
          <Fragment key={quest.id}>
            <ListItem
              disabled={quest.completedToday}
              variant="transparent"
              size="md"
              className={cn(
                "px-3 py-2.5 hover:bg-white/5 transition-all duration-300",
                quest.completedToday && "opacity-50",
                !quest.completedToday && "cursor-pointer",
              )}
              onClick={() => handleQuestClick(quest)}
            >
              <div className="flex items-start w-full gap-3">
                {/* Icon and Small Screen Badges */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <span className="w-12 h-12 rounded border border-border bg-background/20 backdrop-blur-md flex items-center justify-center">
                    <QuestIcon className="w-5 h-5 text-foreground" />
                  </span>
                  {/* Badges beneath icon on small screens */}
                  <span className="flex flex-col gap-1 items-center sm:hidden">
                    <PointsBadge
                      points={quest.campaignPoints}
                      completed={quest.completed}
                      className="bg-neutral-950/40"
                    />
                    <GemsBadge
                      gems={quest.gems}
                      completed={quest.completed}
                      className="bg-neutral-950/40"
                    />
                  </span>
                </div>

                {/* Main Content */}
                <div className={listItemTextVariants()}>
                  <span className="font-suisse font-medium text-sm text-foreground leading-5 cursor-inherit line-clamp-2">
                    {quest.name}
                  </span>
                  <div className="font-suisse text-xs text-muted-foreground leading-4 cursor-inherit space-y-2">
                    {quest.caption && (
                      <p className="text-sm text-muted-foreground leading-5">
                        {quest.caption}
                      </p>
                    )}
                    {/* Perpetual Rewards Indicator */}
                    {quest.perpetualRewardsEligible && (
                      <PerpetualRewardsBadge />
                    )}
                    <div className="flex flex-wrap gap-2.5">
                      {quest.refreshPeriodHours !== null &&
                        quest.completionCount !== undefined &&
                        quest.completionCount > 0 && (
                          <div className="flex items-center gap-1">
                            <IterationCcw className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {quest.completionCount} completion
                              {quest.completionCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Badges on larger screens */}
                <div className="hidden sm:flex flex-col items-end gap-1">
                  <PointsBadge
                    className="self-end bg-neutral-950/40"
                    points={quest.campaignPoints}
                    completed={quest.completed}
                  />
                  <GemsBadge
                    className="self-end bg-neutral-950/40"
                    gems={quest.gems}
                    completed={quest.completed}
                  />
                </div>
              </div>
            </ListItem>
            <div className="flex-1 h-px bg-foreground opacity-5" />
          </Fragment>
        );
      })}
      <QuestInteractionHandler
        game={gameData}
        completeQuest={handleQuestComplete}
        setInProgressQuest={setInProgressQuest}
        inProgressQuest={inProgressQuest}
      />
    </div>
  );
};
